'use client';

import { FileText, Play } from 'lucide-react';
import { type ReactNode, type Ref, useEffect, useRef, useState } from 'react';
import { BaseLineMusic } from '@/app/components/icons/BaseLineMusic';
import { ProductLineAiVideo } from '@/app/components/icons/ProductLineAiVideo';
import { ProductLinePic } from '@/app/components/icons/ProductLinePic';
import { MediaWithWatermark } from '@/app/components/shared/MediaWithWatermark';
import { SITE } from '@/app/constants';
import { useHoverPlayVideo } from '@/app/hooks';
import { cn } from '@/app/utils';
import { getDownloadUrl } from '../../apis';
import type { UploadClawFileResult } from '../../types';

interface MediaAssetCardProps {
  file: UploadClawFileResult;
  clawId?: string;
  badge?: ReactNode;
  onClick?: () => void;
  onResolvedPreviewUrl?: (path: string, url: string) => void;
}

const IconPlaceholder = ({ children }: { children: ReactNode }) => (
  <div className="absolute inset-0 flex size-full items-center justify-center text-system-white40">{children}</div>
);

const PreviewFallback = ({ fileType }: { fileType: UploadClawFileResult['file_type'] }) => {
  if (fileType === 'image') {
    return (
      <div className="flex size-full items-center justify-center text-system-white40">
        <ProductLinePic className="size-8" />
      </div>
    );
  }

  if (fileType === 'video') {
    return (
      <div className="flex size-full items-center justify-center text-system-white40">
        <ProductLineAiVideo className="size-8" />
      </div>
    );
  }

  if (fileType === 'audio') {
    return (
      <div className="flex size-full flex-col items-center justify-center gap-2 text-system-white40">
        <div className="flex size-14 items-center justify-center rounded-12 bg-system-white08">
          <BaseLineMusic className="size-7" />
        </div>
        <div className="text-[11px] text-system-white24 uppercase tracking-[0.12em]">Preview</div>
      </div>
    );
  }

  return (
    <div className="flex size-full flex-col items-center justify-center gap-2 text-system-white40">
      <div className="flex size-14 items-center justify-center rounded-12 bg-system-white08">
        <FileText className="size-7" />
      </div>
      <div className="text-[11px] text-system-white24 uppercase tracking-[0.12em]">Preview</div>
    </div>
  );
};

interface ImagePreviewProps {
  fileName: string;
  previewUrl: string;
  isReady: boolean;
  onReady: (size: { width: number; height: number }) => void;
  onError: () => void;
}

const ImagePreview = ({ fileName, previewUrl, isReady, onReady, onError }: ImagePreviewProps) => (
  <>
    <img
      alt={fileName}
      src={previewUrl}
      className={cn('size-full object-contain transition-opacity', isReady ? 'opacity-100' : 'opacity-0')}
      loading="eager"
      decoding="async"
      fetchPriority="high"
      onLoad={(e) => {
        const t = e.currentTarget;
        onReady({ width: t.naturalWidth || 16, height: t.naturalHeight || 9 });
      }}
      onError={onError}
    />
    {!isReady ? (
      <IconPlaceholder>
        <ProductLinePic className="size-8" />
      </IconPlaceholder>
    ) : null}
  </>
);

interface VideoPreviewProps {
  previewUrl: string;
  videoRef: Ref<HTMLVideoElement>;
  isReady: boolean;
  isHovering: boolean;
  onReady: (size: { width: number; height: number }) => void;
  onError: () => void;
}

const VideoPreview = ({ previewUrl, videoRef, isReady, isHovering, onReady, onError }: VideoPreviewProps) => (
  <>
    <video
      ref={videoRef}
      className={cn('size-full object-contain transition-opacity', isReady ? 'opacity-100' : 'opacity-0')}
      loop
      playsInline
      preload="auto"
      src={previewUrl}
      onLoadedMetadata={(e) => {
        const v = e.currentTarget;
        onReady({ width: v.videoWidth || 16, height: v.videoHeight || 9 });
      }}
      onError={onError}
    >
      <track kind="captions" />
    </video>
    {!isReady ? (
      <IconPlaceholder>
        <ProductLineAiVideo className="size-8" />
      </IconPlaceholder>
    ) : null}
    <div
      className={cn(
        'absolute inset-0 bg-black/20 transition-opacity',
        isHovering && isReady && 'pointer-events-none opacity-0',
      )}
    />
    <div
      className={cn(
        'absolute inset-0 flex items-center justify-center transition-opacity',
        isHovering && isReady && 'pointer-events-none opacity-0',
      )}
    >
      <div className="flex size-9 items-center justify-center rounded-full bg-black/40 text-system-white">
        <Play className="ml-0.5 size-4 fill-current" />
      </div>
    </div>
  </>
);

type PreviewStatus = 'idle' | 'ready' | 'error';

export const MediaAssetCard = ({ file, clawId, badge, onClick, onResolvedPreviewUrl }: MediaAssetCardProps) => {
  const isVisual = file.file_type === 'image' || file.file_type === 'video';
  const fileName = file.file_name || file.file_path.split('/').filter(Boolean).pop() || file.file_path;
  const [isVideoHovering, setIsVideoHovering] = useState(false);
  const [localPreviewUrl, setLocalPreviewUrl] = useState(file.preview_url);
  const [previewStatus, setPreviewStatus] = useState<PreviewStatus>('idle');
  const [mediaSize, setMediaSize] = useState<{ width: number; height: number }>({ width: 16, height: 9 });
  const requestedRef = useRef(false);
  const { videoRef, handleMouseEnter, handleMouseLeave } = useHoverPlayVideo({
    disabled: file.file_type !== 'video',
    resetOnLeave: true,
    onMouseEnter: () => setIsVideoHovering(true),
    onMouseLeave: () => setIsVideoHovering(false),
  });
  const resolvedPreviewUrl = file.preview_url || localPreviewUrl;
  const isPreviewReady = previewStatus === 'ready';

  useEffect(() => {
    setPreviewStatus('idle');
    setMediaSize({ width: 16, height: 9 });
  }, [resolvedPreviewUrl]);

  useEffect(() => {
    requestedRef.current = false;
    setLocalPreviewUrl(file.preview_url);
  }, [clawId, file.file_path, file.preview_url]);

  useEffect(() => {
    if (file.preview_url) {
      setLocalPreviewUrl(file.preview_url);
      requestedRef.current = true;
      return;
    }

    if (!clawId || !isVisual || requestedRef.current) return;

    requestedRef.current = true;

    getDownloadUrl({
      query: {
        claw_id: clawId,
        file_path: file.file_path,
      },
    }).then(({ data, error }) => {
      if (error || !data?.download_url) {
        requestedRef.current = false;
        return;
      }

      setLocalPreviewUrl(data.download_url);
      onResolvedPreviewUrl?.(file.file_path, data.download_url);
    });
  }, [clawId, file.file_path, file.preview_url, isVisual, onResolvedPreviewUrl]);

  let previewContent: ReactNode = <PreviewFallback fileType={file.file_type} />;

  if (resolvedPreviewUrl && isVisual) {
    if (file.file_type === 'image') {
      previewContent = (
        <MediaWithWatermark
          width={mediaSize.width}
          height={mediaSize.height}
          showWatermark={SITE === 'china'}
          className="max-h-full max-w-full"
        >
          <ImagePreview
            fileName={fileName}
            previewUrl={resolvedPreviewUrl}
            isReady={isPreviewReady}
            onReady={(size) => {
              setMediaSize(size);
              setPreviewStatus('ready');
            }}
            onError={() => setPreviewStatus('error')}
          />
        </MediaWithWatermark>
      );
    } else {
      previewContent = (
        <MediaWithWatermark
          width={mediaSize.width}
          height={mediaSize.height}
          showWatermark={SITE === 'china'}
          className="max-h-full max-w-full"
        >
          <VideoPreview
            previewUrl={resolvedPreviewUrl}
            videoRef={videoRef}
            isReady={isPreviewReady}
            isHovering={isVideoHovering}
            onReady={(size) => {
              setMediaSize(size);
              setPreviewStatus('ready');
            }}
            onError={() => setPreviewStatus('error')}
          />
        </MediaWithWatermark>
      );
    }
  }

  return (
    <div className="overflow-hidden rounded-12 border border-system-white08 bg-[rgba(255,255,255,0.06)]">
      <div
        className="relative flex aspect-[16/9] w-full cursor-pointer items-center justify-center overflow-hidden bg-[rgba(255,255,255,0.04)] text-left"
        onClick={onClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {badge}
        {previewContent}
      </div>

      <div className="px-3 py-2">
        <div className="line-clamp-2 break-all text-system-white text-xs leading-5" title={file.file_path}>
          {file.file_path}
        </div>
      </div>
    </div>
  );
};
